import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { ssoRegister } from '../../Common/Utils/SsoSession';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #061b1f 0%, #0e3a44 50%, #0a8294 100%);
  padding: 60px 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 580px;
  background: #fff;
  border-radius: 16px;
  padding: 48px 44px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  animation: ${fadeUp} 0.5s ease both;

  @media (max-width: 640px) {
    padding: 32px 24px;
  }
`;

const Header = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 18px;
`;
const LogoIcon = styled.div`
  width: 36px; height: 36px; border-radius: 9px;
  background: linear-gradient(135deg, #38bdf8, #0ea5e9);
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-weight: 900; font-size: 0.95rem; letter-spacing: -1px;
`;
const Brand = styled.span`
  font-size: 0.88rem; font-weight: 800; color: #0e7490; letter-spacing: 1px; text-transform: uppercase;
`;

const Title = styled.h1`
  font-size: 1.6rem; font-weight: 900; margin: 0 0 6px; color: #0f172a; letter-spacing: -0.4px;
`;
const Sub = styled.p`
  margin: 0 0 24px; color: #64748b; font-size: 0.92rem;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;
const Label = styled.label`
  display: block; margin-bottom: 6px; font-size: 0.85rem; font-weight: 700; color: #334155;
  span.req { color: #dc2626; margin-left: 4px; }
`;
const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus { outline: none; border-color: #0891b2; box-shadow: 0 0 0 3px rgba(8,145,178,0.15); }
`;
const Hint = styled.div`
  margin-top: 4px; font-size: 0.78rem; color: ${({ $err }) => $err ? '#dc2626' : '#64748b'};
`;

const Row = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Submit = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 6px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #0891b2, #0e7490);
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(8,145,178,0.35); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const Foot = styled.div`
  margin-top: 18px; text-align: center; font-size: 0.88rem; color: #64748b;
  a { color: #0891b2; font-weight: 700; text-decoration: none; margin-left: 6px; }
  a:hover { text-decoration: underline; }
`;

const SsoNote = styled.div`
  margin: 18px 0; padding: 12px 14px;
  background: #ecfeff; border: 1px solid #a5f3fc; border-radius: 10px;
  color: #0e7490; font-size: 0.83rem; display: flex; gap: 8px;
  svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px; }
`;

const ErrorMsg = styled.div`
  background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c;
  border-radius: 10px; padding: 10px 14px; font-size: 0.88rem; margin-bottom: 14px;
`;

export default function JoinPage() {
  const history = useHistory();
  const [form, setForm] = useState({
    user_account: '', user_pwd: '', pw_check: '',
    user_name: '', user_ex_email: '', user_mobile: ''
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');

    if (!form.user_account || form.user_account.length < 3) return setError('아이디는 3자 이상 입력하세요.');
    if (!form.user_pwd || form.user_pwd.length < 6) return setError('비밀번호는 6자 이상 입력하세요.');
    if (form.user_pwd !== form.pw_check) return setError('비밀번호가 일치하지 않습니다.');
    if (!form.user_name) return setError('이름을 입력하세요.');
    if (!form.user_ex_email) return setError('이메일을 입력하세요.');

    setBusy(true);
    try {
      const r = await ssoRegister({
        user_account: form.user_account,
        user_name: form.user_name,
        user_pwd: form.user_pwd,
        user_ex_email: form.user_ex_email,
        user_mobile: form.user_mobile,
      });
      if (r && r.success) {
        alert('회원가입이 완료되었습니다. 로그인해주세요.');
        history.replace('/Login');
      } else {
        setError(r?.message || '회원가입에 실패했습니다.');
      }
    } catch (err) {
      setError('회원가입 중 오류가 발생했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageWrapper>
      <Card as="form" onSubmit={submit}>
        <Header>
          <LogoIcon>AN</LogoIcon>
          <Brand>ANTSNEST LMS</Brand>
        </Header>
        <Title>회원가입</Title>
        <Sub>앤츠네스트 SSO 통합 계정을 생성합니다.</Sub>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <Field>
          <Label>아이디 <span className="req">*</span></Label>
          <Input value={form.user_account} onChange={onChange('user_account')}
            placeholder="3자 이상의 영문/숫자" autoComplete="username" />
          <Hint>로그인에 사용됩니다. 가입 후 변경 불가</Hint>
        </Field>

        <Row>
          <Field>
            <Label>비밀번호 <span className="req">*</span></Label>
            <Input type="password" value={form.user_pwd} onChange={onChange('user_pwd')}
              placeholder="6자 이상" autoComplete="new-password" />
          </Field>
          <Field>
            <Label>비밀번호 확인 <span className="req">*</span></Label>
            <Input type="password" value={form.pw_check} onChange={onChange('pw_check')}
              placeholder="다시 입력" autoComplete="new-password" />
          </Field>
        </Row>

        <Field>
          <Label>이름 <span className="req">*</span></Label>
          <Input value={form.user_name} onChange={onChange('user_name')} placeholder="실명" />
        </Field>

        <Field>
          <Label>이메일 <span className="req">*</span></Label>
          <Input type="email" value={form.user_ex_email} onChange={onChange('user_ex_email')}
            placeholder="example@antsnest.co.kr" />
          <Hint>비밀번호 찾기 등에 사용됩니다.</Hint>
        </Field>

        <Field>
          <Label>휴대폰 번호 (선택)</Label>
          <Input value={form.user_mobile} onChange={onChange('user_mobile')} placeholder="010-0000-0000" />
        </Field>

        <SsoNote>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
          <div>가입한 계정은 앤츠네스트의 다른 서비스(SSO 연동)에서도 동일하게 사용할 수 있습니다.</div>
        </SsoNote>

        <Submit type="submit" disabled={busy}>{busy ? '가입 처리 중...' : '가입하기'}</Submit>

        <Foot>
          이미 계정이 있으신가요?<Link to="/Login">로그인</Link>
        </Foot>
      </Card>
    </PageWrapper>
  );
}
